import type { HTMLAttributes, ReactNode } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/cn";

export type AlertTone = "info" | "success" | "warning" | "danger";

export type AlertProps = HTMLAttributes<HTMLDivElement> & {
  tone?: AlertTone;
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
};

const toneClasses: Record<
  AlertTone,
  { container: string; icon: string; title: string }
> = {
  info: {
    container: "border-brand-200 bg-brand-50",
    icon: "text-brand-500",
    title: "text-brand-700",
  },
  success: {
    container: "border-success-500/20 bg-success-50",
    icon: "text-success-500",
    title: "text-success-600",
  },
  warning: {
    container: "border-warning-500/20 bg-warning-50",
    icon: "text-warning-500",
    title: "text-warning-600",
  },
  danger: {
    container: "border-danger-500/20 bg-danger-50",
    icon: "text-danger-500",
    title: "text-danger-600",
  },
};

const defaultIcons: Record<AlertTone, ReactNode> = {
  info: <Info className="h-5 w-5" />,
  success: <CheckCircle2 className="h-5 w-5" />,
  warning: <AlertTriangle className="h-5 w-5" />,
  danger: <AlertCircle className="h-5 w-5" />,
};

export function Alert({
  tone = "info",
  title,
  description,
  icon,
  children,
  className,
  ...rest
}: AlertProps) {
  const cls = toneClasses[tone];
  const effectiveIcon = icon ?? defaultIcons[tone];

  return (
    <div
      role="alert"
      className={cn("rounded-lg border p-4", cls.container, className)}
      {...rest}
    >
      <div className="flex gap-3">
        <span className={cn("mt-0.5 flex-shrink-0", cls.icon)} aria-hidden="true">
          {effectiveIcon}
        </span>
        <div className="flex-1 space-y-0.5">
          {title ? (
            <div className={cn("text-caption font-semibold", cls.title)}>
              {title}
            </div>
          ) : null}
          {description ? (
            <div className="text-caption text-ink-muted">{description}</div>
          ) : null}
          {children}
        </div>
      </div>
    </div>
  );
}
